// src/redux/storeOrdersSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  doc,
  getDoc,
  updateDoc,
  setDoc,
  serverTimestamp,
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
  FieldPath,
} from "firebase/firestore";
import { db } from "../firebase";
import { AppDispatch } from "./store";

/* ═══════════════════════════════════════════════════════════
   STORE ORDERS SLICE
   Namespaced ("storeOrders") order lifecycle for store products.
   Writes to the "StoreOrders" collection (separate from the
   marketplace "Orders") and captures the extra fields the store
   checkout produces: extras, delivery fee, and a full status
   lifecycle (confirm → pay → deliver / cancel / refund) exposed
   as small thunks so the UI never hand-rolls updateDoc calls.
═══════════════════════════════════════════════════════════ */

const STORE_ORDERS_COLLECTION = "ProductOrders";

// ---------------------- //
// TYPES
// ---------------------- //
export interface StoreOrder {
  id: string;
  guestId: string;
  hostId: string;
  brandId?: string; // 🔥 which store this order belongs to
  items: any[];
  extras?: any[];

  subtotal: number;
  totalQty: number;
  totalPaid: number;

  guestsFee: number;
  hostsFee: number;
  hostProfit: number;
  platformProfit: number;
  deliveryFee?: number;

  paymentId: string;

  deliveryDate?: string | null;
  deliveryTime?: string | null;
  deliveryOption?: "now" | "selfpickup" | "scheduled" | null;

  isConfirmed: boolean;
  isCancelled: boolean;
  isRefunded: boolean;
  isPaid: boolean;
  isDelivered: boolean;

  createdAt?: any;
  updatedAt?: any;
  [key: string]: any;
}

// ---------------------- //
// UPSERT ORDER
// ---------------------- //
export const upsertStoreOrder = createAsyncThunk<
  StoreOrder,
  Partial<StoreOrder> & { paymentId: string },
  { rejectValue: string }
>("storeOrders/upsert", async (orderData, { rejectWithValue }) => {
  try {
    if (!orderData.paymentId) throw new Error("Missing paymentId");

    const orderId = orderData.paymentId;
    const docRef = doc(db, STORE_ORDERS_COLLECTION, orderId);

    const existingSnap = await getDoc(docRef);

    if (existingSnap.exists()) {
      await updateDoc(docRef, {
        ...orderData,
        updatedAt: serverTimestamp(),
      });

      const existing = existingSnap.data() as StoreOrder;
      const { id: _ignore1, ...restA } = existing;
      const { id: _ignore2, ...restB } = orderData;

      return { id: orderId, ...restA, ...restB } as StoreOrder;
    } else {
      const payload: Omit<StoreOrder, "id"> = {
        guestId: orderData.guestId!,
        hostId: orderData.hostId!,
        brandId: orderData.brandId ?? "",
        items: orderData.items || [],
        extras: orderData.extras || [],

        subtotal: orderData.subtotal || 0,
        totalQty: orderData.totalQty || 0,
        totalPaid: orderData.totalPaid || 0,

        guestsFee: orderData.guestsFee || 0,
        hostsFee: orderData.hostsFee || 0,
        hostProfit: orderData.hostProfit || 0,
        platformProfit: orderData.platformProfit || 0,
        deliveryFee: orderData.deliveryFee || 0,

        paymentId: orderData.paymentId,

        deliveryDate: orderData.deliveryDate || null,
        deliveryTime: orderData.deliveryTime || null,
        deliveryOption: orderData.deliveryOption || null,

        isConfirmed: true,
        isCancelled: false,
        isRefunded: false,
        isPaid: orderData.isPaid ?? false,
        isDelivered: false,

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(docRef, payload, { merge: true });
      return { id: orderId, ...payload } as StoreOrder;
    }
  } catch (err: any) {
    console.error("🔥 upsertStoreOrder error:", err);
    return rejectWithValue(err.message || "Failed to upsert store order");
  }
});

// ---------------------- //
// STATUS LIFECYCLE HELPERS
// One thunk per transition so components just dispatch intent and
// never assemble raw Firestore field maps.
// ---------------------- //
const patchOrder = async (orderId: string, patch: Record<string, any>) => {
  const ref = doc(db, STORE_ORDERS_COLLECTION, orderId);
  await updateDoc(ref, { ...patch, updatedAt: serverTimestamp() });
  const snap = await getDoc(ref);
  return { id: orderId, ...(snap.data() as Omit<StoreOrder, "id">) } as StoreOrder;
};

export const markStoreOrderPaid = createAsyncThunk<
  StoreOrder,
  { orderId: string; totalPaid?: number },
  { rejectValue: string }
>("storeOrders/markPaid", async ({ orderId, totalPaid }, { rejectWithValue }) => {
  try {
    const patch: Record<string, any> = { isPaid: true };
    if (typeof totalPaid === "number") patch.totalPaid = totalPaid;
    return await patchOrder(orderId, patch);
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});



export const markStoreItemsDelivered = createAsyncThunk<
  StoreOrder,
  { orderId: string; brandIds: string[]; role?: "seller" | "buyer" },
  { rejectValue: string }
>("storeOrders/markItemsDelivered", async (
  { orderId, brandIds, role = "seller" },
  { rejectWithValue }
) => {
  try {
    if (!brandIds?.length) throw new Error("No brand selected");

    const ref = doc(db, STORE_ORDERS_COLLECTION, orderId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error("Order not found");

    const raw = snap.data() as any;
    if (!Array.isArray(raw.brands)) throw new Error("Order has no brands[]");

    const patch =
      role === "buyer"
        ? { buyerReceived: true }
        : { sellerDelivered: true, orderDelivered: true };

    const wanted = new Set(brandIds);

    /* brand row only — items[] stay exactly as the webhook wrote them */
    const brands = raw.brands.map((b: any) =>
      wanted.has(b.brandId)
        ? { ...b, deliveryStatus: { ...(b.deliveryStatus ?? {}), ...patch } }
        : b
    );

    /* order is done only when every brand has all three flags; the
       length check stops [] reading as vacuously true */
    const isDelivered =
      brands.length > 0 &&
      brands.every(
        (b: any) =>
          b.deliveryStatus?.sellerDelivered === true &&
          b.deliveryStatus?.orderDelivered === true &&
          b.deliveryStatus?.buyerReceived === true
      );

    return await patchOrder(orderId, { brands, isDelivered });
  } catch (err: any) {
    console.error("🔥 markStoreItemsDelivered:", err);
    return rejectWithValue(err.message);
  }
});



export const markStoreItemDelivered = createAsyncThunk<
  StoreOrder,
  { orderId: string; listingId: string; colorId?: any; sizeId?: any; role?: "seller" | "buyer" },
  { rejectValue: string }
>("storeOrders/markItemDelivered", async (
  { orderId, listingId, colorId, sizeId, role = "seller" },
  { rejectWithValue }
) => {
  try {
    const ref = doc(db, STORE_ORDERS_COLLECTION, orderId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error("Order not found");

    const patch =
      role === "buyer"
        ? { buyerReceived: true }
        : { sellerDelivered: true, orderDelivered: true };

    const raw = snap.data() as any;
    const stampItem = (i: any) => {
      const hit =
        i.listingId === listingId &&
        (colorId === undefined || (i.colorId ?? null) === (colorId ?? null)) &&
        (sizeId === undefined || (i.sizeId ?? null) === (sizeId ?? null));
      return hit
        ? { ...i, deliveryStatus: { ...(i.deliveryStatus ?? {}), ...patch } }
        : i;
    };
    const allShippedOf = (list: any[]) =>
      list.length > 0 && list.every((i: any) => i.deliveryStatus?.orderDelivered === true);

    if (raw.brands) {
      const brands = raw.brands.map((b: any) => ({ ...b, items: (b.items ?? []).map(stampItem) }));
      const allOut = allShippedOf(brands.flatMap((b: any) => b.items ?? []));
      return await patchOrder(orderId, { brands, isDelivered: allOut });
    }

    // legacy fallback — old docs still on a flat top-level items[]
    const items = (raw.items ?? []).map(stampItem);
    return await patchOrder(orderId, { items, isDelivered: allShippedOf(items) });
  } catch (err: any) {
    console.error("🔥 markStoreItemDelivered:", err);
    return rejectWithValue(err.message);
  }
});


export const cancelStoreOrder = createAsyncThunk<
  StoreOrder,
  string,
  { rejectValue: string }
>("storeOrders/cancel", async (orderId, { rejectWithValue }) => {
  try {
    return await patchOrder(orderId, { isCancelled: true, isConfirmed: false });
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

export const refundStoreOrder = createAsyncThunk<
  StoreOrder,
  string,
  { rejectValue: string }
>("storeOrders/refund", async (orderId, { rejectWithValue }) => {
  try {
    return await patchOrder(orderId, { isRefunded: true });
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

// ---------------------- //
// FETCH GUEST ORDERS
// ---------------------- //
export const fetchStoreGuestOrders = createAsyncThunk<
  StoreOrder[],
  string,
  { rejectValue: string }
>("storeOrders/fetchGuestOrders", async (guestId, { rejectWithValue }) => {
  try {
    const q = query(
      collection(db, STORE_ORDERS_COLLECTION),
      where("guestId", "==", guestId)
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => {
      const raw = d.data() as any;
      const items = (raw.brands ?? raw.items ?? []).flatMap((b: any) =>
        b.items ? b.items.map((i: any) => ({ ...i, brandId: b.brandId })) : [b]
      );
      return { ...raw, id: d.id, items } as StoreOrder;
    });
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

/* ProductOrders has no top-level hostId — a single order can span
   several sellers. The host's slice lives at hostProfits[hostId],
   so we query that map key: it exists only on orders this host is
   actually part of. */
const hostOrdersQuery = (hostId: string) =>
  query(
    collection(db, STORE_ORDERS_COLLECTION),
    where(new FieldPath("hostProfits", hostId, "hostsFee"), ">=", 0)
  );

/** Only the lines this host is responsible for, plus their own
    earnings hoisted to the top level so the UI can read them flat. */
const scopeOrderToHost = (raw: any, id: string, hostId: string): StoreOrder => {
  const all = (raw.brands ?? raw.items ?? []).flatMap((b: any) =>
    b.items ? b.items.map((i: any) => ({ ...i, brandId: b.brandId })) : [b]
  );
  const mine = all.filter((i: any) => i.hostId === hostId);
  const cut = raw.hostProfits?.[hostId] ?? {};
  return {
    ...raw,
    id,
    hostId,
    items: mine.length ? mine : all,
    hostsFee: cut.hostsFee ?? 0,
    hostProfit: cut.hostProfit ?? 0,
    deliveryFee: cut.deliveryFee ?? 0,
    saleTotal: cut.saleTotal ?? 0,
  } as StoreOrder;
};

// ---------------------- //
// FETCH HOST ORDERS
// ---------------------- //
export const fetchStoreHostOrders = createAsyncThunk<
  StoreOrder[],
  string,
  { rejectValue: string }
>("storeOrders/fetchHostOrders", async (hostId, { rejectWithValue }) => {
  try {
    const snap = await getDocs(hostOrdersQuery(hostId));
    return snap.docs.map((d) => scopeOrderToHost(d.data(), d.id, hostId));
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

// ---------------------- //
// REAL-TIME: HOST
export const listenToStoreHostOrders =
  (hostId: string) => (dispatch: AppDispatch) => {
    if (!hostId) return;

    const q = query(
      collection(db, STORE_ORDERS_COLLECTION),
      where(new FieldPath("hostProfits", hostId, "hostsFee"), ">=", 0)
    );

    return onSnapshot(
      q,
      (snapshot) => {
        const orders = snapshot.docs.map((d) => {
          const raw = d.data() as any;
          const all = (raw.brands ?? raw.items ?? []).flatMap((b: any) =>
            b.items ? b.items.map((i: any) => ({ ...i, brandId: b.brandId })) : [b]
          );
          const mine = all.filter((i: any) => i.hostId === hostId);
          const cut = raw.hostProfits?.[hostId] ?? {};
          /* brand rows this host owns — where deliveryStatus actually lives */
          const myBrands = (raw.brands ?? []).filter((b: any) => b.hostId === hostId);
          return {
            ...raw,
            id: d.id,
            hostId,
            myBrands,
            items: mine.length ? mine : all,
            hostsFee: cut.hostsFee ?? 0,
            hostProfit: cut.hostProfit ?? 0,
            deliveryFee: cut.deliveryFee ?? 0,
            saleTotal: cut.saleTotal ?? 0,
          } as StoreOrder;
        });
        dispatch(setHostOrders(orders));
      },
      (err) => console.error("🔥 listenToStoreHostOrders:", err)
    );
  };



  /* ═══════════════════════════════════════════════════════════
   BRAND INSIGHTS — customers / analytics / finance / growth
   All four are keyed by brandId, but orders are keyed by hostId
   and items[] carry only a listingId. StoreProducts/{listingId}
   is the bridge: it holds both brandId and hostId. So each
   listener runs in two stages — watch the brand's catalogue to
   learn the listingIds + owning hostId, then watch that host's
   orders and keep only the lines in that catalogue.
═══════════════════════════════════════════════════════════ */
const STORE_PRODUCTS_COLLECTION = "StoreProducts";
const DAY = 86_400_000;

export interface BrandCustomer {
  guestId: string; orders: number; units: number; totalSpent: number;
  firstOrderAt: number | null; lastOrderAt: number | null;
  status: "active" | "lapsed";
}
export interface TopProduct {
  listingId: string; name: string; photo?: string;
  units: number; revenue: number; orders: number;
}

/* the checkout prices a variant off the listing, so a line is worth
   the variant rate when there is one, else the base rate */
const lineTotal = (listing: any, item: any) =>
  Number(listing?.variants?.[item?.colorId]?.rate ?? listing?.rate ?? 0) *
  Number(item?.qty ?? 0);

const msOf = (ts: any): number | null => {
  if (!ts) return null;
  const n = Number(ts?.seconds ? ts.seconds * 1000 : ts?.toMillis?.() ?? ts);
  return Number.isFinite(n) ? n : null;
};
const pctChange = (now: number, prev: number) =>
  prev > 0 ? Number((((now - prev) / prev) * 100).toFixed(1)) : null;

interface ScopedOrder {
  id: string; raw: any; items: any[];
  revenue: number; units: number; ratio: number; at: number | null;
}

const watchBrandOrders = (
  brandId: string,
  emit: (orders: ScopedOrder[], listings: Map<string, any>) => void
) => {
  if (!brandId) return () => {};

  let listings = new Map<string, any>();
  let hostId = "";
  let unsubOrders: (() => void) | null = null;

  const stopOrders = () => { if (unsubOrders) { unsubOrders(); unsubOrders = null; } };

  const startOrders = () => {
    stopOrders();
    if (!hostId) { emit([], listings); return; }

    unsubOrders = onSnapshot(
      hostOrdersQuery(hostId),
      (snap) => {
        const scoped: ScopedOrder[] = [];
        snap.docs.forEach((d) => {
          const raw = d.data() as any;
          const all = (raw.brands ?? raw.items ?? []).flatMap((b: any) =>
            b.items ? b.items.map((i: any) => ({ ...i, brandId: b.brandId })) : [b]
          );
          const mine = all.filter((i: any) => listings.has(i.listingId));
          if (!mine.length) return;

          let revenue = 0, units = 0;
          mine.forEach((i: any) => {
            revenue += lineTotal(listings.get(i.listingId), i);
            units += Number(i.qty ?? 0);
          });

          const hostSale = Number(raw.hostProfits?.[hostId]?.saleTotal ?? 0);
          scoped.push({
            id: d.id, raw, items: mine,
            revenue: Number(revenue.toFixed(2)), units,
            ratio: hostSale > 0 ? Math.min(1, revenue / hostSale) : 1,
            at: msOf(raw.createdAt),
          });
        });
        emit(scoped, listings);
      },
      (err) => console.error("🔥 watchBrandOrders/orders:", err)
    );
  };

  const unsubProducts = onSnapshot(
    query(collection(db, STORE_PRODUCTS_COLLECTION), where("brandId", "==", brandId)),
    (snap) => {
      listings = new Map(snap.docs.map((d) => [d.id, { listingId: d.id, ...d.data() }]));
      const nextHost = String(
        snap.docs.find((d) => (d.data() as any).hostId)?.data()?.hostId ?? ""
      );
      if (nextHost !== hostId) { hostId = nextHost; startOrders(); }
      else if (!unsubOrders) startOrders();
    },
    (err) => console.error("🔥 watchBrandOrders/products:", err)
  );

  return () => { unsubProducts(); stopOrders(); };
};

/* ── 1. CUSTOMERS ── */
export const listenToBrandCustomers =
  (brandId: string) => (dispatch: AppDispatch) =>
    watchBrandOrders(brandId, (orders) => {
      const byGuest = new Map<string, BrandCustomer>();
      const cutoff = Date.now() - 90 * DAY;

      orders.forEach((o) => {
        if (o.raw.isCancelled || !o.raw.guestId) return;
        const c = byGuest.get(o.raw.guestId) ?? {
          guestId: o.raw.guestId, orders: 0, units: 0, totalSpent: 0,
          firstOrderAt: null, lastOrderAt: null, status: "lapsed" as const,
        };
        c.orders += 1;
        c.units += o.units;
        c.totalSpent = Number((c.totalSpent + o.revenue).toFixed(2));
        if (o.at !== null) {
          c.firstOrderAt = c.firstOrderAt === null ? o.at : Math.min(c.firstOrderAt, o.at);
          c.lastOrderAt  = c.lastOrderAt  === null ? o.at : Math.max(c.lastOrderAt, o.at);
        }
        byGuest.set(o.raw.guestId, c);
      });

      dispatch(setBrandCustomers(
        [...byGuest.values()]
          .map((c) => ({ ...c, status: (c.lastOrderAt ?? 0) >= cutoff ? "active" as const : "lapsed" as const }))
          .sort((a, b) => b.totalSpent - a.totalSpent)
      ));
    });

/* ── 2. ANALYTICS ──
   No views or conversion rate: there's no page-view collection,
   so fulfilmentRate is the real metric in its place. */
export const listenToBrandAnalytics =
  (brandId: string) => (dispatch: AppDispatch) =>
    watchBrandOrders(brandId, (orders, listings) => {
      const paid = orders.filter((o) => o.raw.isPaid && !o.raw.isCancelled);
      const revenue = paid.reduce((s, o) => s + o.revenue, 0);

      const per = new Map<string, TopProduct>();
      paid.forEach((o) => o.items.forEach((i: any) => {
        const l = listings.get(i.listingId);
        const p = per.get(i.listingId) ?? {
          listingId: i.listingId, name: l?.name ?? "Untitled product",
          photo: l?.photos?.[0], units: 0, revenue: 0, orders: 0,
        };
        p.units += Number(i.qty ?? 0);
        p.revenue = Number((p.revenue + lineTotal(l, i)).toFixed(2));
        p.orders += 1;
        per.set(i.listingId, p);
      }));

      const delivered = paid.filter((o) => o.raw.isDelivered).length;

      dispatch(setBrandAnalytics({
        orders: paid.length,
        units: paid.reduce((s, o) => s + o.units, 0),
        revenue: Number(revenue.toFixed(2)),
        avgOrderValue: paid.length ? Number((revenue / paid.length).toFixed(2)) : 0,
        deliveredOrders: delivered,
        cancelledOrders: orders.filter((o) => o.raw.isCancelled).length,
        fulfilmentRate: paid.length ? Number(((delivered / paid.length) * 100).toFixed(1)) : 0,
        topProducts: [...per.values()].sort((a, b) => b.revenue - a.revenue).slice(0, 10),
      }));
    });

/* ── 3. FINANCE ──
   hostProfits[hostId] covers the seller's whole slice of an order,
   which may span several of their brands, so fees are scaled by
   this brand's share (ratio). */
export const listenToBrandFinance =
  (brandId: string) => (dispatch: AppDispatch) =>
    watchBrandOrders(brandId, (orders) => {
      let gross = 0, fees = 0, delivery = 0, refunded = 0, pending = 0;
      const transactions: any[] = [];

      orders.forEach((o) => {
        const key = Object.keys(o.raw.hostProfits ?? {})[0];
        const cut = o.raw.hostProfits?.[key] ?? {};
        const fee  = Number(((cut.hostsFee ?? 0) * o.ratio).toFixed(2));
        const ship = Number(((cut.deliveryFee ?? 0) * o.ratio).toFixed(2));

        if (o.raw.isCancelled) {
          transactions.push({ id: o.id, type: "Cancelled", at: o.at, amount: 0, status: "completed" });
          return;
        }
        if (o.raw.isRefunded) {
          refunded = Number((refunded + o.revenue).toFixed(2));
          transactions.push({ id: o.id, type: "Refund", at: o.at, amount: -o.revenue, status: "completed" });
          return;
        }

        gross    = Number((gross + o.revenue).toFixed(2));
        fees     = Number((fees + fee).toFixed(2));
        delivery = Number((delivery + ship).toFixed(2));
        if (!o.raw.isDelivered) pending = Number((pending + (o.revenue - fee + ship)).toFixed(2));

        transactions.push({
          id: o.id, type: "Sale", at: o.at, amount: o.revenue,
          status: o.raw.isDelivered ? "completed" : "pending",
        });
      });

      transactions.sort((a, b) => (b.at ?? 0) - (a.at ?? 0));
      dispatch(setBrandFinance({
        gross, fees, delivery, refunded, pending,
        net: Number((gross - fees + delivery).toFixed(2)),
        transactions,
      }));
    });

/* ── 4. GROWTH — last 30 days vs the 30 before ──
   Funnel and traffic-by-channel need visit tracking that isn't
   collected anywhere, so they're omitted rather than invented. */
export const listenToBrandGrowth =
  (brandId: string) => (dispatch: AppDispatch) =>
    watchBrandOrders(brandId, (orders) => {
      const now = Date.now();
      const winStart = now - 30 * DAY;
      const prevStart = now - 60 * DAY;

      const billable = orders.filter((o) => o.raw.isPaid && !o.raw.isCancelled && o.at !== null);

      let revenueNow = 0, revenuePrev = 0, ordersNow = 0, ordersPrev = 0;
      const daily = new Map<string, { dateKey: string; revenue: number; orders: number }>();
      const firstSeen = new Map<string, number>();

      billable.forEach((o) => {
        const at = o.at as number;
        const seen = firstSeen.get(o.raw.guestId);
        if (seen === undefined || at < seen) firstSeen.set(o.raw.guestId, at);

        if (at >= winStart) {
          revenueNow += o.revenue; ordersNow += 1;
          const k = new Date(at).toISOString().slice(0, 10);
          const row = daily.get(k) ?? { dateKey: k, revenue: 0, orders: 0 };
          row.revenue = Number((row.revenue + o.revenue).toFixed(2));
          row.orders += 1;
          daily.set(k, row);
        } else if (at >= prevStart) {
          revenuePrev += o.revenue; ordersPrev += 1;
        }
      });

      const buyers = new Set(billable.filter((o) => (o.at as number) >= winStart).map((o) => o.raw.guestId));
      let newCustomers = 0, returningCustomers = 0;
      buyers.forEach((g) => ((firstSeen.get(g) ?? 0) >= winStart ? newCustomers++ : returningCustomers++));

      dispatch(setBrandGrowth({
        revenueNow: Number(revenueNow.toFixed(2)),
        revenuePrev: Number(revenuePrev.toFixed(2)),
        revenueChangePct: pctChange(revenueNow, revenuePrev),
        ordersNow, ordersPrev,
        ordersChangePct: pctChange(ordersNow, ordersPrev),
        newCustomers, returningCustomers,
        repeatRatePct: buyers.size ? Number(((returningCustomers / buyers.size) * 100).toFixed(1)) : 0,
        daily: [...daily.values()].sort((a, b) => a.dateKey.localeCompare(b.dateKey)),
      }));
    });




/* ── 5. RAW ORDER FEED — this brand's scoped orders as-is, no rollup ── */
export const listenToBrandOrders =
  (brandId: string) => (dispatch: AppDispatch) =>
    watchBrandOrders(brandId, (orders) => {
      dispatch(setBrandOrders(
        orders.map((o) => ({
          ...o.raw,
          id: o.id,
          items: o.items,
          revenue: o.revenue,
          units: o.units,
        }))
      ));
    });


// ---------------------- //
// REAL-TIME: GUEST
// ---------------------- //
export const listenToStoreGuestOrders =
  (guestId: string) => (dispatch: AppDispatch) => {
    if (!guestId) return;
    const q = query(
      collection(db, STORE_ORDERS_COLLECTION),
      where("guestId", "==", guestId)
    );

    return onSnapshot(q, (snapshot) => {
      const orders: StoreOrder[] = snapshot.docs.map((d) => {
        const raw = d.data() as any;
        const items = (raw.brands ?? raw.items ?? []).flatMap((b: any) =>
          b.items ? b.items.map((i: any) => ({ ...i, brandId: b.brandId })) : [b]
        );
        return { ...raw, id: d.id, items } as StoreOrder;
      });
      dispatch(setGuestOrders(orders));
    });
  };

// ---------------------- //
// REAL-TIME: BY SESSION ID
// ---------------------- //
export const listenToStoreOrderBySessionId =
  (sessionId: string, userId: string) => (dispatch: AppDispatch) => {
    if (!sessionId || !userId) return;

    const ref = doc(db, STORE_ORDERS_COLLECTION, sessionId);

    return onSnapshot(ref, (docSnap) => {
      if (!docSnap.exists()) {
        dispatch(setOrderById(null));
        return;
      }
      const data = docSnap.data() as Omit<StoreOrder, "id">;

      if (data.guestId !== userId) {
        dispatch(setOrderById(null));
        return;
      }

      dispatch(setOrderById({ ...data, id: docSnap.id } as StoreOrder));
    });
  };

// ---------------------- //
// REAL-TIME: SINGLE ORDER
// ---------------------- //
export const listenToStoreOrderById =
  (orderId: string) => (dispatch: AppDispatch) => {
    if (!orderId) return;

    const ref = doc(db, STORE_ORDERS_COLLECTION, orderId);

    return onSnapshot(ref, (docSnap) => {
      if (!docSnap.exists()) {
        dispatch(setOrderById(null));
        return;
      }
      const data = docSnap.data() as Omit<StoreOrder, "id">;
      dispatch(setOrderById({ ...data, id: docSnap.id } as StoreOrder));
    });
  };

// ---------------------- //
// SLICE
// ---------------------- //
const storeOrdersSlice = createSlice({
  name: "storeOrders",
  initialState: {
    guestOrders: [] as StoreOrder[],
    hostOrders: [] as StoreOrder[],
    orderById: null as StoreOrder | null,
    loading: false,
    error: null as string | null,

    /* brand insights */
    brandCustomers: [] as BrandCustomer[],
    brandAnalytics: {
      orders: 0, units: 0, revenue: 0, avgOrderValue: 0,
      deliveredOrders: 0, cancelledOrders: 0, fulfilmentRate: 0,
      topProducts: [] as TopProduct[],
    },
    brandFinance: {
      gross: 0, fees: 0, delivery: 0, net: 0, refunded: 0, pending: 0,
      transactions: [] as any[],
    },
    brandGrowth: {
      revenueNow: 0, revenuePrev: 0, revenueChangePct: null as number | null,
      ordersNow: 0, ordersPrev: 0, ordersChangePct: null as number | null,
      newCustomers: 0, returningCustomers: 0, repeatRatePct: 0,
      daily: [] as { dateKey: string; revenue: number; orders: number }[],
    },
    brandOrders: [] as any[],
  },
  reducers: {
    setGuestOrders: (state, action: PayloadAction<StoreOrder[]>) => {
      state.guestOrders = action.payload;
    },
    setHostOrders: (state, action: PayloadAction<StoreOrder[]>) => {
      state.hostOrders = action.payload;
    },
    setOrderById: (state, action: PayloadAction<StoreOrder | null>) => {
      state.orderById = action.payload;
    },
    setBrandCustomers: (s, a: PayloadAction<BrandCustomer[]>) => { s.brandCustomers = a.payload; },
    setBrandAnalytics: (s, a: PayloadAction<any>) => { s.brandAnalytics = a.payload; },
    setBrandFinance:   (s, a: PayloadAction<any>) => { s.brandFinance = a.payload; },
    setBrandGrowth:    (s, a: PayloadAction<any>) => { s.brandGrowth = a.payload; },
    setBrandOrders:    (s, a: PayloadAction<any[]>) => { s.brandOrders = a.payload; },
  },
  extraReducers: (builder) => {
    /* keep any order that comes back from a transition thunk in sync
       across all three buckets it might live in */
    const syncOrder = (state: any, order: StoreOrder) => {
      const gi = state.guestOrders.findIndex((o: StoreOrder) => o.id === order.id);
      if (gi >= 0) state.guestOrders[gi] = order;

      const hi = state.hostOrders.findIndex((o: StoreOrder) => o.id === order.id);
      if (hi >= 0) state.hostOrders[hi] = order;

      if (state.orderById?.id === order.id) state.orderById = order;
    };

    builder
      .addCase(upsertStoreOrder.pending, (state) => {
        state.loading = true;
      })
      .addCase(upsertStoreOrder.fulfilled, (state, action) => {
        const idx = state.guestOrders.findIndex((o) => o.id === action.payload.id);
        if (idx >= 0) state.guestOrders[idx] = action.payload;
        else state.guestOrders.push(action.payload);
        state.loading = false;
      })
      .addCase(upsertStoreOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to upsert store order";
      })

      /* lifecycle transitions */
      .addCase(markStoreOrderPaid.fulfilled, (s, a) => syncOrder(s, a.payload))
      .addCase(markStoreItemDelivered.fulfilled, (s, a) => syncOrder(s, a.payload))
      .addCase(markStoreItemsDelivered.fulfilled, (s, a) => syncOrder(s, a.payload))

      .addCase(cancelStoreOrder.fulfilled, (s, a) => syncOrder(s, a.payload))
      .addCase(refundStoreOrder.fulfilled, (s, a) => syncOrder(s, a.payload));
  },
});

export const {
  setGuestOrders, setHostOrders, setOrderById,
  setBrandCustomers, setBrandAnalytics, setBrandFinance, setBrandGrowth, setBrandOrders,
} = storeOrdersSlice.actions;

export default storeOrdersSlice.reducer;
